// ST41 — live stream-content viewer. Subscribes to WS /streams/{id}?token=… via the
// existing transport/stream.ts consumer and renders frames by media_type:
//   • text/json chunks → a running token log,
//   • audio (pcm) chunks → a level meter,
//   • binary chunks     → a frame/byte counter + hex preview of the first bytes.
//
// The WS frame format is FROZEN (§6.4) — this only READS it. Closing the viewer
// unsubscribes the WS (no socket leak). The reducer is pure + unit-tested; the
// component is a thin shell around it.

import { useEffect, useReducer, useRef } from 'react';
import { connectStream } from '../transport/stream';
import { decodeStreamPayload, type StreamWsFrame, isStreamStatus } from '../api/events';
import type { WsClient } from '../transport/ws';

const MAX_LOG = 200;

export interface ChannelView {
  channel_id: number;
  /** How the channel's payloads decode. */
  kind: 'text' | 'audio' | 'binary';
  frames: number;
  bytes: number;
  /** Recent decoded text lines (json/text channels). */
  log: string[];
  /** 0..1 signal level for audio channels (RMS of the last chunk). */
  level: number;
  /** Hex preview of the first bytes of the most recent binary chunk. */
  hex: string;
}

export interface StreamViewerState {
  status: 'connecting' | 'active' | 'closed' | 'errored';
  bytes: number;
  seq: number;
  error?: string;
  channels: Record<number, ChannelView>;
  invalid: number;
}

export function initStreamState(): StreamViewerState {
  return { status: 'connecting', bytes: 0, seq: 0, channels: {}, invalid: 0 };
}

export function bytesToHex(bytes: Uint8Array, limit = 16): string {
  const n = Math.min(bytes.length, limit);
  let out = '';
  for (let i = 0; i < n; i++) out += bytes[i].toString(16).padStart(2, '0') + ' ';
  if (bytes.length > limit) out += '…';
  return out.trim();
}

// RMS level of a PCM-ish unsigned byte buffer, normalized to 0..1 (128 = silence).
export function audioLevel(bytes: Uint8Array): number {
  if (bytes.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < bytes.length; i++) {
    const d = (bytes[i] - 128) / 128;
    sum += d * d;
  }
  return Math.min(1, Math.sqrt(sum / bytes.length));
}

type Action =
  | { kind: 'frame'; frame: StreamWsFrame; mediaType?: string }
  | { kind: 'invalid' }
  | { kind: 'reset' };

// Classify a channel by its decoded payload + the descriptor media_type hint.
function classify(encoding: 'json' | 'base64', mediaType?: string): ChannelView['kind'] {
  if (encoding === 'json') return 'text';
  if (mediaType && mediaType.startsWith('audio/')) return 'audio';
  if (mediaType && (mediaType.startsWith('text/') || mediaType.includes('json'))) return 'text';
  return 'binary';
}

export function reduceStream(state: StreamViewerState, action: Action): StreamViewerState {
  if (action.kind === 'reset') return initStreamState();
  if (action.kind === 'invalid') return { ...state, invalid: state.invalid + 1 };

  const frame = action.frame;
  if (isStreamStatus(frame)) {
    const status: StreamViewerState['status'] =
      frame.state === 'ERRORED'
        ? 'errored'
        : frame.state === 'CLOSED' || frame.state === 'CLOSING'
          ? 'closed'
          : 'active';
    return { ...state, status, bytes: frame.bytes || state.bytes, seq: frame.seq || state.seq, error: frame.error };
  }

  // chunk frame
  const cid = frame.channel_id;
  const prev: ChannelView =
    state.channels[cid] ?? { channel_id: cid, kind: 'binary', frames: 0, bytes: 0, log: [], level: 0, hex: '' };
  const decoded = decodeStreamPayload(frame);
  const kind = classify(frame.encoding, action.mediaType);
  const next: ChannelView = { ...prev, kind, frames: prev.frames + 1 };

  if (decoded.kind === 'json') {
    const line = typeof decoded.value === 'string' ? decoded.value : JSON.stringify(decoded.value);
    next.log = [...prev.log, line].slice(-MAX_LOG);
    next.bytes = prev.bytes + line.length;
  } else {
    const b = decoded.bytes;
    next.bytes = prev.bytes + b.length;
    next.hex = bytesToHex(b);
    if (kind === 'audio') next.level = audioLevel(b);
  }

  return {
    ...state,
    status: state.status === 'connecting' ? 'active' : state.status,
    seq: Math.max(state.seq, frame.seq),
    channels: { ...state.channels, [cid]: next },
  };
}

export function StreamViewer({
  wsUrl,
  mediaType,
  onClose,
  WebSocketImpl,
}: {
  wsUrl: string;
  mediaType?: string;
  onClose?: () => void;
  WebSocketImpl?: typeof WebSocket;
}) {
  const [state, dispatch] = useReducer(reduceStream, undefined, initStreamState);
  const clientRef = useRef<WsClient | null>(null);

  useEffect(() => {
    dispatch({ kind: 'reset' });
    const client = connectStream({
      wsUrl,
      WebSocketImpl,
      onChunk: (frame) => dispatch({ kind: 'frame', frame, mediaType }),
      onStatus: (frame) => dispatch({ kind: 'frame', frame, mediaType }),
      onInvalid: () => dispatch({ kind: 'invalid' }),
    });
    clientRef.current = client;
    // Unsubscribe on unmount / url change — no socket leak (ST41 DoD).
    return () => {
      client.close();
      clientRef.current = null;
    };
  }, [wsUrl, mediaType, WebSocketImpl]);

  const channels = Object.values(state.channels).sort((a, b) => a.channel_id - b.channel_id);

  return (
    <div className="stream-viewer">
      <div className="stream-viewer-head">
        <span className={`badge badge-${state.status === 'errored' ? 'bad' : state.status === 'closed' ? 'muted' : 'ok'}`}>
          {state.status}
        </span>
        <span className="muted small">seq {state.seq}</span>
        <span className="muted small">{humanBytes(state.bytes)}</span>
        {state.invalid > 0 && <span className="muted small">{state.invalid} dropped</span>}
        {onClose && (
          <button className="btn btn-sm" onClick={onClose} aria-label="Close stream viewer">
            Close
          </button>
        )}
      </div>
      {state.error && <div className="form-error small">{state.error}</div>}
      {channels.length === 0 ? (
        <div className="muted small">Waiting for frames…</div>
      ) : (
        <div className="stream-channels">
          {channels.map((c) => (
            <ChannelPanel key={c.channel_id} ch={c} />
          ))}
        </div>
      )}
    </div>
  );
}

function ChannelPanel({ ch }: { ch: ChannelView }) {
  return (
    <div className="stream-channel" data-kind={ch.kind}>
      <div className="stream-channel-head muted small">
        <b>ch {ch.channel_id}</b> · {ch.kind} · {ch.frames} frames · {humanBytes(ch.bytes)}
      </div>
      {ch.kind === 'text' && (
        <pre className="stream-log">{ch.log.join('\n') || '…'}</pre>
      )}
      {ch.kind === 'audio' && (
        <div className="stream-level" aria-label={`audio level ${Math.round(ch.level * 100)}%`}>
          <div className="stream-level-bar" style={{ width: `${Math.round(ch.level * 100)}%` }} />
        </div>
      )}
      {ch.kind === 'binary' && <code className="mono small stream-hex">{ch.hex || '—'}</code>}
    </div>
  );
}

function humanBytes(n: number): string {
  if (!n) return '0 B';
  const u = ['B', 'KB', 'MB', 'GB'];
  let i = 0;
  let v = n;
  while (v >= 1024 && i < u.length - 1) {
    v /= 1024;
    i++;
  }
  return `${v.toFixed(v < 10 && i > 0 ? 1 : 0)} ${u[i]}`;
}
